import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch, getGetMeQueryKey } from "@workspace/api-client-react";
import { PauseCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { canAccessPage, readAccess } from "@/lib/access";

// Faixa no topo de todas as telas do painel enquanto a loja está pausada (Pedro, 28/10/2026). O interruptor «Fechar Loja
// Temporariamente» inverteu o gesto de «Loja ativa», e lojas que ligavam o botão todo dia para abrir ficaram pausadas
// sem saber. «Abrir a loja» grava o mesmo isActive do interruptor. A loja aberta em outro aparelho some daqui na volta
// à janela (o padrão do React Query) ou no minuto seguinte, enquanto a faixa está na tela.
export function FaixaLojaPausada() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  // Quem não tem a página Configurações (equipe) não grava o status da loja: a faixa vai sem o botão.
  const podeAbrir = canAccessPage(readAccess(user), "configuracoes");
  const { data: settings } = useQuery<any>({
    queryKey: ["settings"],
    queryFn: () => customFetch("/api/settings"),
    staleTime: 60_000,
    refetchInterval: (q) => ((q.state.data as any)?.isActive === false ? 60_000 : false),
  });
  const [abrindo, setAbrindo] = useState(false);
  if (settings?.isActive !== false) return null;

  const abrir = async () => {
    setAbrindo(true);
    try {
      const salvo = await customFetch<any>("/api/settings", { method: "PUT", body: JSON.stringify({ isActive: true }) });
      queryClient.setQueryData(["settings"], (prev: any) => (prev ? { ...prev, ...salvo } : salvo));
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    } catch (err: any) {
      toast({ title: "Não consegui abrir a loja", description: err?.data?.error?.message || err?.message || "Tente de novo.", variant: "destructive" });
    } finally {
      setAbrindo(false);
    }
  };

  return (
    <div
      className="sticky top-14 md:top-0 z-30 bg-amber-100 dark:bg-amber-950/80 border-b border-amber-300 dark:border-amber-800 text-amber-950 dark:text-amber-100 px-4 py-2 flex items-center justify-between gap-3 text-[13px] sm:text-sm leading-snug"
      role="status"
      data-testid="faixa-loja-pausada"
    >
      <span className="flex items-center gap-2 font-medium min-w-0">
        <PauseCircle className="w-4 h-4 shrink-0" />
        Sua loja está pausada e não está recebendo pedidos.
      </span>
      {podeAbrir && (
        <Button size="sm" onClick={abrir} disabled={abrindo} className="shrink-0" data-testid="faixa-abrir-loja">
          Abrir a loja
        </Button>
      )}
    </div>
  );
}
